import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  PracticeSessionInfo,
  SelfPracticeInputs,
  SelfPracticeQuestionContent,
  SelfPracticeSessionState,
} from "@/types/conductSelfPractice";
import { ApiResponse, AzureUploadUrl } from "@/types/global";
import { SelfPracticeReport, SelfPracticeReportDetails } from "@/types/studentSelfPracticeReport";

export default class SelfPracticeApi extends ApiVerified {
  async getSelfPracticeCourseSubject(
    userId: number,
    subjectChapterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<number>(
        `self-practice/get-self-practice-course-subject`,
        { params: { userId: userId, subjectChapterId: subjectChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createSelfPractice(selfPracticeInputs: SelfPracticeInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `self-practice/create-session`,
        selfPracticeInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async startSelfPractice(selfPracticeId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<SelfPracticeSessionState>(
        `self-practice/start-self-practice`,
        {
          SelfPracticeId: selfPracticeId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSelfPracticeQuestionContent(
    subjectChapterId: number,
    conceptId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SelfPracticeQuestionContent>(
        `self-practice/get-self-practice-question-content`,
        { params: { subjectChapterId: subjectChapterId, conceptId: conceptId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSubjectiveSelfPracticeAnswerUploadUrl(
    selfPracticeId: number,
    questionId: number,
    contentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `self-practice/get-subjective-self-practice-answer-upload-url`,
        {
          params: {
            selfPracticeId: selfPracticeId,
            questionId: questionId,
            contentType: contentType,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveSelfPracticeSessionState(sessionState: SelfPracticeSessionState): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `self-practice/save-self-practice-session-state`,
        sessionState,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async pauseSelfPractice(selfPracticeId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(`self-practice/pause-self-practice`, {
        SelfPracticeId: selfPracticeId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async endSelfPractice(sessionState: SelfPracticeSessionState): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `self-practice/end-self-practice`,
        sessionState,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async endPausedPracticeSession(selfPracticeId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `self-practice/end-paused-practice-session`,
        {
          SelfPracticeId: selfPracticeId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSelfPracticePausedSessions(
    userId: number,
    classId: number,
    courseSubjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PracticeSessionInfo>>(
        `self-practice/get-practice-paused-sessions`,
        {
          params: { userId: userId, classId: classId, courseSubjectId: courseSubjectId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSelfPracticeCompletedSessions(
    userId: number,
    classId: number,
    courseSubjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PracticeSessionInfo>>(
        `self-practice/get-practice-completed-sessions`,
        {
          params: { userId: userId, classId: classId, courseSubjectId: courseSubjectId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentSelfPracticeReport(selfPracticeId: number, userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SelfPracticeReport>(
        `self-practice/get-self-practice-report`,
        {
          params: { selfPracticeId: selfPracticeId, userId: userId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentSelfPracticeReportDetails(
    selfPracticeId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SelfPracticeReportDetails>(
        `self-practice/get-self-practice-report-details`,
        {
          params: { selfPracticeId: selfPracticeId, userId: userId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
